import { mergeAttributes, Node, nodeInputRule } from '@tiptap/core';
import { Selection, NodeSelection } from '@tiptap/pm/state';
import { isDocx, isPdf } from '../../app/src/utilities/fileExtensions';
import { isPublicPageApp } from '../../app/src/utilities/isPublicPageApp';
import constants from '../../app/src/utilities/constants';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    stImage: {
      /**
       * Add an image
       */
      setImage: (options: {
        src: string;
        alt?: string;
        title?: string;
        width: number;
        height: number;
      }) => ReturnType;

      setAllEditorImages: () => void;

      displayFullScreenImage: (id: string) => void;
    };
  }
}

export const inputRegex = /(?:^|\s)(!\[(.+|:?)]\((\S+)(?:(?:\s+)["'](\S+)["'])?\))$/;

export interface StImageOptions {
  plugins?: any[];
  unsplashBaseUrl: string;
  displayImageFullScreen?: any;
  getFileIdFromUrl?: any;
  unsplashObjectByImgSrc?: any;
  objectByImgSrc?: any;
  findChildren?: Function;
  inline?: boolean;
  allowBase64?: boolean;
  HTMLAttributes?: Record<string, any>;
  tagName: string;
}

export default Node.create<StImageOptions>({
  name: 'st-image',

  addOptions() {
    return {
      inline: false,
      allowBase64: false,
      HTMLAttributes: {},
      unsplashBaseUrl: '',
      tagName: 'st-image',
    };
  },

  inline() {
    return this.options.inline;
  },

  group() {
    return `stImageGroup ${this.options.inline ? 'inline' : 'block'}`;
  },

  draggable: true,

  addStorage() {
    return {
      images: [],
    };
  },

  addAttributes() {
    return {
      imgsrc: {
        default: null,
      },
      'data-id': {
        default: null,
      },
      'blur-hash': {
        default: null,
      },
      height: {
        default: constants.stImageDefaultHeight,
        parseHTML: (el) => {
          return (
            Math.abs(parseInt(el.getAttribute('height') ?? '', 10)) ||
            constants.stImageDefaultHeight
          );
        },
      },
      width: {
        default: constants.stImageDefaultWidth,
        parseHTML: (el) => {
          return (
            Math.abs(parseInt(el.getAttribute('width') ?? '', 10)) || constants.stImageDefaultWidth
          );
        },
      },
      style: {
        default: '',
        parseHTML: (element) => {
          return `width: ${Math.abs(parseInt(element.getAttribute('width') ?? '', 10)) ?? constants.stImageDefaultWidth}px`;
        },
      },
      alt: {
        default: null,
      },
    };
  },

  parseHTML() {
    return [
      {
        tag: this.options.allowBase64 ? 'img[src]' : 'st-image[imgsrc]:not([imgsrc^="data:"])',
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    return [
      this.options.tagName,
      mergeAttributes(this.options.HTMLAttributes || {}, HTMLAttributes),
    ];
  },

  addCommands() {
    return {
      setImage:
        (options) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              ...options,
              style: `width: ${Math.abs(options?.width) ?? constants.stImageDefaultWidth}px`,
            },
          });
        },

      setAllEditorImages: () => () => {
        this.storage.images = [];
        const { state } = this.editor;
        const { doc } = state;
        const nodesWithoutId = this.options.findChildren?.(doc, (node: any) => {
          return (
            node.type.name === this.name ||
            isPdf(node.attrs.fileType) ||
            isDocx(node.attrs.fileType)
          );
        });

        nodesWithoutId.forEach(({ node, pos }: { node: any; pos: number }) => {
          const { imgsrc, alt } = node.attrs;
          if (isPdf(node.attrs.fileType) || isDocx(node.attrs.fileType)) {
            const id = node.attrs.url?.split('/').pop();
            // pdf & docx handler
            const payload = {
              id,
              ...node.attrs,
              type: node.attrs.fileType,
              name: node.attrs.fileName,
              pos,
            };
            this.storage.images.push(payload);
          } else if (imgsrc.includes(this.options.unsplashBaseUrl)) {
            const unsplashImg = this.options.unsplashObjectByImgSrc?.(imgsrc, alt);
            this.storage.images.push({
              ...unsplashImg,
              pos,
            });
          } else {
            const id = imgsrc.split('/').pop();
            const imageObject = { ...(this.options.objectByImgSrc?.(id, imgsrc, alt) || {}), pos };
            this.storage.images.push(imageObject);
          }
        });
      },

      displayFullScreenImage: (id) => () => {
        this.editor.commands.setAllEditorImages();
        if (this.editor.storage?.MouseOver?.dragHandleBlockId) {
          // @ts-ignore
          this.editor.commands.hideDragHandle();
        }

        let resourceId = '';
        // @ts-ignore
        if (this.editor.resourceType === 'card') {
          // @ts-ignore
          resourceId = this.editor.resourceId;
          // @ts-ignore
        } else if (this.editor.parentResourceType === 'card') {
          // @ts-ignore
          resourceId = this.editor.parentResourceId;
        }

        const images = this.editor.storage['st-image'].images;

        const attachmentIndex = images.findIndex((a: any) => a.id === id);
        if (attachmentIndex !== -1 && isDocx(images[attachmentIndex].type) && !isPublicPageApp()) {
          images.splice(attachmentIndex, 1);
        }

        this.options.displayImageFullScreen?.(images, id, resourceId);
      },
    };
  },

  addKeyboardShortcuts() {
    return {
      Space: ({ editor }) => {
        const { state } = editor.view;
        const { selection } = state;
        const near = Selection.near(selection.$from) as NodeSelection;
        if (near instanceof NodeSelection) {
          const { node } = near;
          if (node.type.name === this.name) {
            const id = this.options.getFileIdFromUrl?.(node.attrs.imgsrc);
            this.editor.commands.displayFullScreenImage(id);
            return true;
          }
        }
        return false;
      },
    };
  },

  addInputRules() {
    return [
      nodeInputRule({
        find: inputRegex,
        type: this.type,
        getAttributes: (match) => {
          const [, , alt, src, title, width, height] = match;

          return { src, alt, title, width, height };
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    if (!this.options.plugins) {
      return [];
    }
    return [...this.options.plugins.map((plugin: any) => plugin({ editor: this.editor }))];
  },
});
